import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import {
  BaseTransaction,
  Response,
  GeneralLedgerTransaction,
  IntegrationLog,
  Pagination,
} from '@ui/src';
import { Category } from '@ui/src/components/accounts-selector/types';
import {
  CalculateTransactionRateResult,
  ExchangeTransactionValidator,
} from '../../types';
import { exchangeTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { transactionApi } from '../../../transactions/store/services';
import { Balance } from '../../../../types/account';

export interface ExchangeTransactionState {
  panelIsOpen: boolean;
  transactionId?: string;
  transaction?: BaseTransaction & {
    exchangeProfitRate?: number;
    exchangeBaseRate?: number;
    exchangeInverseActualRate: number;
    exchangeInverseBaseRate: number;
    exchangeInverseRate: number;
    exchangeRateTolerance?: number;
    disableExternalTransactionCreate?: boolean;
    children?: {
      id: string;
    }[];
  };
  withoutCorrectionTransaction?: BaseTransaction & {
    exchangeProfitRate?: number;
    exchangeBaseRate?: number;
    exchangeInverseActualRate: number;
    exchangeInverseBaseRate: number;
    exchangeInverseRate: number;
    children?: {
      id: string;
    }[];
  };
  validators?: ExchangeTransactionValidator;
  calculateResult?: CalculateTransactionRateResult;
  accountCategories?: Category[];
  glTransactions?: GeneralLedgerTransaction[];
  fromAccountBalances?: Balance[];
  toAccountBalances?: Balance[];
  syncedTransactions?: BaseTransaction[];
  integrationLogs?: IntegrationLog[];
  integrationLogsPagination?: Pagination;
}

const initialState: ExchangeTransactionState = {
  panelIsOpen: true,
  transaction: undefined,
  transactionId: undefined,
};

export const exchangeTransactionSlice = createSlice({
  name: 'exchangeTransaction',
  initialState,
  reducers: {
    setCalculatedResult: (
      state,
      action: PayloadAction<CalculateTransactionRateResult>,
    ) => {
      state.calculateResult = action.payload;
    },
    changePanelState: (
      state,
      action: PayloadAction<{ state: boolean; transactionId?: string }>,
    ) => {
      state.panelIsOpen = action.payload.state;

      if (action.payload.state) {
        state.transactionId = action.payload.transactionId;
      } else {
        state.transactionId = undefined;
      }
    },

    clearExchangeTransactionState: (state) => {
      state.transaction = initialState.transaction;
      state.transactionId = undefined;
      state.panelIsOpen = false;
      state.calculateResult = undefined;
      state.fromAccountBalances = undefined;
      state.toAccountBalances = undefined;
      state.syncedTransactions = undefined;
      state.integrationLogs = undefined;
      state.integrationLogsPagination = undefined;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getExchangeTransactionById
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{
          data: BaseTransaction & {
            exchangeProfitRate?: number;
            exchangeInverseActualRate: number;
            exchangeInverseBaseRate: number;
            exchangeInverseRate: number;
          };
        }>,
      ) => {
        const prevTransaction = state.transaction;
        const nextTransaction = action.payload.data;

        if (!prevTransaction) {
          state.transaction = nextTransaction;
        } else if (!isEqual(prevTransaction, nextTransaction)) {
          state.transaction = nextTransaction;
        }
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getExchangeTransactionValidators
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: ExchangeTransactionValidator }>,
      ) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getSyncedTransactions
        .matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        state.syncedTransactions = action.payload.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      statusesTransactionsApi.endpoints.changeTransactionStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        if (state.transaction) {
          const nextTransaction = { ...state.transaction };

          nextTransaction.baseellaStatus = action.payload.data.baseellaStatus;
          nextTransaction.latestQueuedStatuses = action.payload.data
            .latestQueuedStatuses
            ? action.payload.data.latestQueuedStatuses
            : nextTransaction.latestQueuedStatuses;

          state.transaction = nextTransaction;
        }
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.updateExchangeTransaction
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{
          data: BaseTransaction & {
            exchangeProfitRate?: number;
            exchangeInverseActualRate: number;
            exchangeInverseBaseRate: number;
            exchangeInverseRate: number;
          };
        }>,
      ) => {
        state.transaction = { ...state.transaction, ...action.payload.data };
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.calculateTransactionRates
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: CalculateTransactionRateResult }>,
      ) => {
        state.calculateResult = action.payload.data;
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.calculateTransactionRatesById
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: CalculateTransactionRateResult }>,
      ) => {
        state.calculateResult = action.payload.data;
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.calculateTransactionRates
        .matchRejected,
      (state) => {
        state.calculateResult = undefined;
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.calculateTransactionRatesById
        .matchRejected,
      (state) => {
        state.calculateResult = undefined;
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getExchangeAccountsCategories
        .matchFulfilled,
      (state, action: PayloadAction<Response<Category>>) => {
        state.accountCategories = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (state, action: PayloadAction<Response<GeneralLedgerTransaction>>) => {
        if (state.transaction) {
          state.glTransactions = action.payload.data.edges.map(
            (item) => item.node,
          );
        }
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getAllFromAccountBalances
        .matchFulfilled,
      (state, action: PayloadAction<Response<Balance>>) => {
        state.fromAccountBalances = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getAllToAccountBalances
        .matchFulfilled,
      (state, action: PayloadAction<Response<Balance>>) => {
        state.toAccountBalances = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      exchangeTransactionsListApi.endpoints.getExchangeIntegrationLogs
        .matchFulfilled,
      (state, action: PayloadAction<Response<IntegrationLog>>) => {
        if (state.transaction) {
          state.integrationLogs = action.payload.data.edges.map(
            (item) => item.node,
          );

          state.integrationLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
  },
});

export const {
  changePanelState,
  clearExchangeTransactionState,
  setCalculatedResult,
} = exchangeTransactionSlice.actions;

export default exchangeTransactionSlice.reducer;
